/**
 * The Cooked Sushi header — mobile drawer, category nav, cart badge
 */
(function () {
  const drawer = document.getElementById('NihooyaHeaderDrawer');
  const openBtn = document.querySelector('[data-nihooya-menu-open]');
  if (!drawer) return;

  const closeEls = drawer.querySelectorAll('[data-nihooya-menu-close]');
  const panel = drawer.querySelector('.nihooya-header__drawer-panel');
  let lastFocus = null;

  function openDrawer() {
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    openBtn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    const closeBtn = drawer.querySelector('.nihooya-header__drawer-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    openBtn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  openBtn?.addEventListener('click', openDrawer);
  closeEls.forEach((el) => el.addEventListener('click', closeDrawer));
  drawer.querySelectorAll('a[href]').forEach((link) => {
    link.addEventListener('click', closeDrawer);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  if (panel) {
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;

      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initCategoryScroll(root) {
    const scrollEl = root.querySelector('[data-nihooya-categories-scroll]');
    const prevBtn = root.querySelector('[data-nihooya-categories-prev]');
    const nextBtn = root.querySelector('[data-nihooya-categories-next]');
    const fadeLeft = root.querySelector('[data-nihooya-categories-fade-left]');
    const fadeRight = root.querySelector('[data-nihooya-categories-fade-right]');
    if (!scrollEl) return;

    function scrollActiveIntoView(behavior = 'smooth') {
      const activeItem = scrollEl.querySelector('.nihooya-header__category--active');
      if (!activeItem) return;
      activeItem.scrollIntoView({ inline: 'center', block: 'nearest', behavior });
    }

    function updateScrollState() {
      const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
      const canScroll = maxScroll > 4;
      const atStart = scrollEl.scrollLeft <= 4;
      const atEnd = scrollEl.scrollLeft >= maxScroll - 4;

      scrollEl.classList.toggle('is-scrollable', canScroll);
      prevBtn?.classList.toggle('is-visible', canScroll && !atStart);
      nextBtn?.classList.toggle('is-visible', canScroll && !atEnd);
      fadeLeft?.classList.toggle('is-visible', canScroll && !atStart);
      fadeRight?.classList.toggle('is-visible', canScroll && !atEnd);
    }

    function scrollByDirection(direction) {
      const amount = Math.max(scrollEl.clientWidth * 0.55, 220);
      scrollEl.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    prevBtn?.addEventListener('click', () => scrollByDirection(-1));
    nextBtn?.addEventListener('click', () => scrollByDirection(1));
    scrollEl.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    updateScrollState();
    scrollActiveIntoView('auto');

    return { scrollActiveIntoView, updateScrollState };
  }

  function initCategoryNav() {
    const root = document.querySelector('[data-nihooya-categories]');
    if (!root) return;

    const links = Array.from(root.querySelectorAll('[data-nihooya-category-link]'));
    const scrollApi = initCategoryScroll(root);
    if (!links.length) return;

    function setActiveLink(activeLink, scroll = true) {
      if (!activeLink) return;

      links.forEach((link) => {
        link.classList.remove('nihooya-header__category--active');
        link.removeAttribute('aria-current');
      });

      activeLink.classList.add('nihooya-header__category--active');
      activeLink.setAttribute('aria-current', 'page');

      if (scroll) {
        scrollApi?.scrollActiveIntoView('smooth');
      }
    }

    function linkFromHash(hash) {
      if (!hash) return null;
      return links.find((link) => {
        try {
          const url = new URL(link.href);
          return url.hash === hash;
        } catch {
          return link.getAttribute('data-category-anchor') === hash.replace('#', '');
        }
      });
    }

    function scrollToAnchor(hash, behavior = 'smooth') {
      const target = document.querySelector(hash);
      if (!target) return false;

      const headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '120',
        10
      );

      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
      window.scrollTo({ top, behavior });
      return true;
    }

    links.forEach((link, index) => {
      link.addEventListener('mouseenter', () => {
        link.classList.add('nihooya-header__category--hover');
      });

      link.addEventListener('mouseleave', () => {
        link.classList.remove('nihooya-header__category--hover');
      });

      link.addEventListener('touchstart', () => {
        link.classList.add('nihooya-header__category--pressed');
      }, { passive: true });

      link.addEventListener('touchend', () => {
        window.setTimeout(() => link.classList.remove('nihooya-header__category--pressed'), 180);
      });

      link.addEventListener('click', (event) => {
        let url;
        try {
          url = new URL(link.href);
        } catch {
          return;
        }

        const hash = url.hash;
        const samePage = window.location.pathname === url.pathname;

        if (hash && samePage) {
          event.preventDefault();
          if (scrollToAnchor(hash)) {
            history.pushState(null, '', hash);
            setActiveLink(link);
          }
          return;
        }

        if (hash && !samePage) {
          return;
        }

        setActiveLink(link, false);
      });

      link.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          links[Math.min(index + 1, links.length - 1)]?.focus();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          links[Math.max(index - 1, 0)]?.focus();
        }
      });
    });

    function syncActiveFromHash() {
      const hash = window.location.hash;
      if (hash) {
        const link = linkFromHash(hash);
        if (link) setActiveLink(link, true);
        return;
      }

      if (window.location.pathname.includes('/pages/menu')) {
        const menuLink = links.find((link) => !link.getAttribute('data-category-anchor'));
        if (menuLink) setActiveLink(menuLink, false);
      }
    }

    window.addEventListener('hashchange', syncActiveFromHash);
    syncActiveFromHash();

    if (window.location.hash) {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          const hash = window.location.hash;
          if (scrollToAnchor(hash, 'auto')) {
            const link = linkFromHash(hash);
            if (link) setActiveLink(link, true);
          }
        }, 120);
      });
    }

    const anchorIds = links
      .map((link) => link.getAttribute('data-category-anchor'))
      .filter(Boolean);

    const sections = anchorIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (sections.length > 1) {
      const headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '120',
        10
      );

      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (!visible) return;

          const link = linkFromHash(`#${visible.target.id}`);
          if (link) setActiveLink(link, true);
        },
        {
          rootMargin: `-${headerOffset + 16}px 0px -55% 0px`,
          threshold: [0.15, 0.35, 0.55]
        }
      );

      sections.forEach((section) => observer.observe(section));
    }
  }

  function initCartBadge() {
    document.querySelectorAll('.nihooya-header__cart-badge').forEach((badge) => {
      const count = parseInt(badge.textContent, 10) || 0;
      badge.classList.toggle('is-empty', count <= 0);
      badge.setAttribute('data-count', String(count));
    });
  }

  function initLanguageSelector() {
    const selector = document.querySelector('[data-nihooya-lang-selector]');
    if (!selector) return;

    const toggle = selector.querySelector('[data-nihooya-lang-toggle]');
    const panel = selector.querySelector('[data-nihooya-lang-panel]');
    const closeTriggers = selector.querySelectorAll('[data-nihooya-lang-close]');
    const options = selector.querySelectorAll('.nihooya-header__lang-option');
    let lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      selector.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      panel?.setAttribute('aria-hidden', 'false');
    }

    function close() {
      selector.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      panel?.setAttribute('aria-hidden', 'true');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function isOpen() {
      return selector.classList.contains('is-open');
    }

    toggle?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (isOpen()) close();
      else open();
    });

    closeTriggers.forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('click', (event) => {
      if (!isOpen()) return;
      if (!selector.contains(event.target)) close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) close();
    });

    options.forEach((option) => {
      option.addEventListener('click', () => {
        close();
      });
    });

    if (panel) {
      panel.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !isOpen()) return;

        const focusable = panel.querySelectorAll(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }
  }

  function init() {
    initCategoryNav();
    initCartBadge();
    initLanguageSelector();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      if (!this.header) return;
      this.setHeaderHeight();
      if (this.getAttribute('data-sticky-type') !== 'none') {
        this.header.classList.add('shopify-section-header-sticky');
      }
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', () => this.setHeaderHeight());
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const threshold = document.body.classList.contains('template-index') ? 48 : 24;
      const scrolled = window.scrollY > threshold;
      this.classList.toggle('is-scrolled', scrolled);
      this.setHeaderHeight();
    }

    setHeaderHeight() {
      if (this.header) {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      }
    }
  });
}